import React, { useState } from "react";
import {
  API_BASE_URL,
  readJsonSafe,
  apiErrorMessage,
  SERVER_UNAVAILABLE_MESSAGE,
} from "../../../utils/api";
import {
  UserIcon,
  LockIcon,
  EyeIcon,
  EyeOffIcon,
  ArrowIcon,
  AlertIcon,
  CheckIcon,
} from "../icons/Icons";

function LoginPage({ onLoginSuccess }) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username.trim()) {
      setError("Please enter your username.");
      return;
    }
    if (!formData.password.trim()) {
      setError("Please enter your password.");
      return;
    }
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
        }),
      });

      const data = await readJsonSafe(response);

      if (!response.ok) {
        const fallback =
          typeof data.detail === "string" && data.detail
            ? data.detail
            : "Login failed";
        throw new Error(apiErrorMessage(response, fallback));
      }

      if (!data.access_token) {
        throw new Error("Login succeeded but no access token was returned");
      }

      /* ─── Store all auth info in localStorage ─── */
      localStorage.setItem("als_token", String(data.access_token || "").trim());
      localStorage.setItem("als_role", (data.role || "").toLowerCase());

      // Teacher-specific fields (backend should return these)
      if (data.role && data.role.toLowerCase() === "teacher") {
        localStorage.setItem(
          "als_teacher_id",
          data.teacher_id || data.user_id || "",
        );
        localStorage.setItem("als_teacher_name", data.name || "");
        localStorage.setItem("als_teacher_campus_id", data.campus_id || "");
        localStorage.setItem(
          "als_teacher_email",
          data.email || formData.username,
        );
        localStorage.removeItem("als_parent_id");
        localStorage.removeItem("als_parent_name");
      } else if (data.role && data.role.toLowerCase() === "parent") {
        localStorage.setItem("als_parent_id", data.parent_id || "");
        localStorage.setItem("als_parent_name", data.name || "");
        localStorage.removeItem("als_teacher_id");
        localStorage.removeItem("als_teacher_name");
        localStorage.removeItem("als_teacher_campus_id");
        localStorage.removeItem("als_teacher_email");
      } else {
        // Admin / superadmin — clear any stale teacher/parent keys
        localStorage.removeItem("als_teacher_id");
        localStorage.removeItem("als_teacher_name");
        localStorage.removeItem("als_teacher_campus_id");
        localStorage.removeItem("als_teacher_email");
        localStorage.removeItem("als_parent_id");
        localStorage.removeItem("als_parent_name");
      }

      localStorage.setItem("als_user_name", data.name || formData.username);

      setIsLoading(false);
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        onLoginSuccess(data.access_token, (data.role || "").toLowerCase());
      }, 1500);
    } catch (err) {
      setIsLoading(false);
      const message = String(err?.message || "");
      setError(
        message.includes("Failed to fetch") || message.includes("NetworkError")
          ? SERVER_UNAVAILABLE_MESSAGE
          : message || SERVER_UNAVAILABLE_MESSAGE,
      );
    }
  };

  return (
    <div className="login-page">
      <div className="login-brand-panel">
        <div className="brand-pattern" />
        <div className="floating-shapes">
          <div className="shape shape-1" />
          <div className="shape shape-2" />
          <div className="shape shape-3" />
          <div className="shape shape-4" />
        </div>
        <div className="brand-content">
          <div className="brand-logo-container">
            <img
              src={process.env.PUBLIC_URL + "/als-logo.png"}
              alt="American Lycetuff DNK School System"
              className="brand-logo"
            />
          </div>
          <h1 className="brand-title">AMERICAN&nbsp;LYCETUFF</h1>
          <div className="brand-subtitle">DNK School System</div>
          <p className="brand-tagline">
            Empowering minds, shaping futures — <em>Excellence in Education</em>
          </p>
        </div>
      </div>
      <div className="login-form-panel">
        <div className="login-form-wrapper">
          <div className="mobile-logo">
            <img
              src={process.env.PUBLIC_URL + "/als-logo.png"}
              alt="ALS Logo"
            />
            <div className="mobile-logo-title">American Lycetuff</div>
          </div>
          <div className="login-header">
            <div className="login-welcome">Welcome Back</div>
            <h2 className="login-heading">Sign in to your Portal</h2>
            <p className="login-description">
              Access your academic dashboard, resources, and more.
            </p>
          </div>
          <form onSubmit={handleSubmit} autoComplete="off">
            {error && (
              <div className="error-message">
                <AlertIcon />
                <p>{error}</p>
              </div>
            )}
            <div className="form-group" style={{ "--i": 2 }}>
              <label className="form-label" htmlFor="username">
                Username
              </label>
              <div className="input-wrapper">
                <input
                  type="text"
                  id="username"
                  name="username"
                  className="form-input"
                  placeholder="Enter your username"
                  value={formData.username}
                  onChange={handleInputChange}
                />
                <UserIcon />
              </div>
            </div>
            <div className="form-group" style={{ "--i": 3 }}>
              <label className="form-label" htmlFor="password">
                Password
              </label>
              <div className="input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  className="form-input"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleInputChange}
                />
                <LockIcon />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  id="toggle-password"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  style={{ zIndex: 5 }}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              className="login-btn"
              id="login-button"
              disabled={isLoading}
            >
              <span>
                {isLoading ? (
                  <>
                    <div className="btn-spinner" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowIcon />
                  </>
                )}
              </span>
            </button>
          </form>
          <div className="login-divider">
            <span>Need Help?</span>
          </div>
          <div className="login-help">
            <p>
              Contact the administration at{" "}
              <a
                href="https://wa.me/923331234567"
                target="_blank"
                rel="noopener noreferrer"
                id="support-whatsapp"
              >
                support@als.edu
              </a>
            </p>
          </div>
        </div>
        <div className="login-footer">
          <p>
            © 2026 American Lycetuff DNK School System. All rights reserved.
          </p>
        </div>
      </div>

      {showSuccess && (
        <div className="success-overlay">
          <div className="success-content">
            <div className="success-icon">
              <CheckIcon />
            </div>
            <h3>Welcome Back!</h3>
            <p>
              You have successfully signed in. Redirecting to your dashboard...
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoginPage;
